import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { Bot, Send, Trophy, UserPlus, Users, XCircle } from "lucide-react";
import type { LeadAutomationInput, LeadAutomationStepInput, LeadInput, LeadsSettings, LeadStatus } from "@booking-seconds/types";
import { Badge, Button, Card, DataTable, Field } from "@booking-seconds/ui";
import { apiFetch } from "./lib/api";
import { OpsHeader, OpsMetricCard, OpsMetricGrid, OpsPageShell } from "./pages/admin/ops-ui";

type LeadSource = {
  id: string;
  name: string;
  slug: string;
  channel: string;
  active: number | boolean;
};

type LeadEventRecord = {
  id: string;
  leadId: string;
  eventType: string;
  message: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  leadName?: string;
  leadEmail?: string;
  leadPhone?: string;
};

type LeadRecord = LeadInput & {
  id: string;
  accountId: string;
  emailNormalized: string;
  phoneNormalized: string;
  statusEnteredAt: string;
  linkedCustomerId: string;
  linkedBookingId: string;
  lastContactedAt: string;
  createdAt: string;
  updatedAt: string;
  events?: LeadEventRecord[];
};

type EmailTemplateRecord = {
  id: string;
  name: string;
  templateType: "transactional" | "marketing";
};

type LeadAutomationRecord = LeadAutomationInput & {
  id: string;
  accountId: string;
  pendingCount: number;
  queuedCount: number;
  sentCount: number;
  failedCount: number;
  nextDueAt: string;
  createdAt: string;
  updatedAt: string;
};

const leadStatuses: Array<{ value: LeadStatus; label: string }> = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "estimate_sent", label: "Estimate Sent" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" }
];

const emptyLeadDraft: LeadInput = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  status: "new",
  sourceId: "",
  sourceName: "",
  originDomain: "",
  pageUrl: "",
  referrerUrl: "",
  utm: {},
  serviceInterest: "",
  note: "",
  estimateSnapshot: {},
  emailConsent: false,
  smsConsent: false,
  lostReason: ""
};

const emptyAutomationStep: LeadAutomationStepInput = {
  id: "",
  delayValue: 5,
  delayUnit: "minutes",
  channel: "email",
  templateId: "",
  smsBody: "Hi {{leadName}}, thanks for reaching out to {{businessName}}. Reply here and we can help.",
  generatedOffer: {
    enabled: false,
    discountType: "percentage",
    discountValue: 10,
    codePrefix: "",
    expirationDays: 7,
    eligibilityMode: "newsletter_first_booking"
  }
};

const emptyAutomationDraft: LeadAutomationInput = {
  name: "New lead quick response",
  triggerStatus: "new",
  sourceIds: [],
  stopOnBooking: true,
  steps: [{ ...emptyAutomationStep, id: "step_1" }],
  active: true
};

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function formatDateTime(value: string) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function leadName(lead: Pick<LeadRecord, "firstName" | "lastName" | "email" | "phone">) {
  return [lead.firstName, lead.lastName].filter(Boolean).join(" ").trim() || lead.email || lead.phone || "Unnamed lead";
}

function statusLabel(status: LeadStatus | string) {
  return leadStatuses.find((item) => item.value === status)?.label ?? String(status).replace(/_/g, " ");
}

function statusTone(status: LeadStatus | string): "neutral" | "success" | "warning" | "danger" | "info" {
  if (status === "won") return "success";
  if (status === "lost") return "danger";
  if (status === "estimate_sent") return "warning";
  if (status === "contacted") return "info";
  return "neutral";
}

function stepDelayMinutes(step: LeadAutomationStepInput) {
  const value = Math.max(1, Number(step.delayValue || 1));
  if (step.delayUnit === "days") return value * 1440;
  if (step.delayUnit === "hours") return value * 60;
  return value;
}

function normalizeGeneratedOffer(
  value: unknown,
  eligibilityMode: "newsletter_first_booking" | "customer_winback" = "newsletter_first_booking"
): LeadAutomationStepInput["generatedOffer"] {
  const current = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  return {
    enabled: Boolean(current.enabled),
    discountType: "percentage" as const,
    discountValue: Math.max(1, Math.min(100, Math.round(Number(current.discountValue ?? 10) || 10))),
    codePrefix: String(current.codePrefix ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 24),
    expirationDays: Math.max(1, Math.min(365, Math.round(Number(current.expirationDays ?? 7) || 7))),
    eligibilityMode: current.eligibilityMode === "customer_winback" ? "customer_winback" as const : eligibilityMode
  };
}

function normalizeAutomationDraft(draft: LeadAutomationInput): LeadAutomationInput {
  return {
    ...draft,
    stopOnBooking: draft.stopOnBooking !== false,
    steps: [...draft.steps]
      .map((step, index) => ({
        ...step,
        id: step.id || `step_${index + 1}`,
        delayValue: Math.max(1, Math.trunc(Number(step.delayValue || 1))),
        templateId: step.channel === "email" ? step.templateId : "",
        smsBody: step.channel === "sms" ? step.smsBody : "",
        generatedOffer: normalizeGeneratedOffer(step.generatedOffer, "newsletter_first_booking")
      }))
      .sort((left, right) => stepDelayMinutes(left) - stepDelayMinutes(right))
  };
}

function csvToList(value: string) {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function listToCsv(value: string[]) {
  return value.join(", ");
}

export function LeadsPage() {
  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Lead operations"
        title="Leads"
        description="Capture prospects, manage the lead pipeline, and automate open-estimate follow-up."
      />
      <div className="communications-tabs">
        {[
          ["overview", "Overview"],
          ["pipeline", "Pipeline"],
          ["capture", "Capture"],
          ["automations", "Automations"],
          ["activity", "Activity"]
        ].map(([path, label]) => (
          <NavLink key={path} to={`/admin/leads/${path}`} className={({ isActive }) => `communications-tab ${isActive ? "is-active" : ""}`}>
            {label}
          </NavLink>
        ))}
      </div>
      <Routes>
        <Route index element={<Navigate to="/admin/leads/overview" replace />} />
        <Route path="overview" element={<LeadsOverview />} />
        <Route path="pipeline" element={<LeadsPipeline />} />
        <Route path="capture" element={<LeadsCapture />} />
        <Route path="automations" element={<LeadsAutomations />} />
        <Route path="activity" element={<LeadsActivity />} />
        <Route path="*" element={<Navigate to="/admin/leads/overview" replace />} />
      </Routes>
    </OpsPageShell>
  );
}

function useLeads() {
  return useQuery({
    queryKey: ["leads"],
    queryFn: () => apiFetch<{ items: LeadRecord[] }>("/api/leads")
  });
}

function useLeadSources() {
  return useQuery({
    queryKey: ["leads", "sources"],
    queryFn: () => apiFetch<{ items: LeadSource[] }>("/api/leads/sources")
  });
}

function LeadsOverview() {
  const leads = useLeads();
  const automations = useQuery({
    queryKey: ["leads", "automations"],
    queryFn: () => apiFetch<{ items: LeadAutomationRecord[] }>("/api/leads/automations")
  });
  const items = asArray<LeadRecord>(leads.data?.items);
  const activeAutomations = asArray<LeadAutomationRecord>(automations.data?.items).filter((item) => item.active);
  const openLeads = items.filter((item) => !["won", "lost"].includes(item.status));
  const estimateLeads = items.filter((item) => item.status === "estimate_sent");

  return (
    <div className="stack">
      <OpsMetricGrid>
        <OpsMetricCard label="Open leads" value={openLeads.length} detail="Not won or lost yet" icon={Users} tone="blue" />
        <OpsMetricCard label="New" value={items.filter((item) => item.status === "new").length} detail="Fresh prospects to review" icon={UserPlus} tone="emerald" />
        <OpsMetricCard label="Open estimates" value={estimateLeads.length} detail="Estimate sent, not closed" icon={Send} tone="amber" />
        <OpsMetricCard label="Won" value={items.filter((item) => item.status === "won").length} detail="Converted lead records" icon={Trophy} tone="emerald" />
        <OpsMetricCard label="Lost" value={items.filter((item) => item.status === "lost").length} detail="Closed without booking" icon={XCircle} tone="rose" />
        <OpsMetricCard label="Active automations" value={activeAutomations.length} detail="Lead follow-up running" icon={Bot} tone="violet" />
      </OpsMetricGrid>
      <Card title="Recent Leads" eyebrow="Pipeline">
        <DataTable
          columns={["Lead", "Status", "Source", "Interest", "Updated"]}
          rows={items.slice(0, 12).map((lead) => [
            leadName(lead),
            <Badge key={`${lead.id}-status`} tone={statusTone(lead.status)}>{statusLabel(lead.status)}</Badge>,
            lead.sourceName || "-",
            lead.serviceInterest || "-",
            formatDateTime(lead.updatedAt)
          ])}
        />
      </Card>
    </div>
  );
}

function LeadsPipeline() {
  const queryClient = useQueryClient();
  const leads = useLeads();
  const sources = useLeadSources();
  const items = asArray<LeadRecord>(leads.data?.items);
  const sourceItems = asArray<LeadSource>(sources.data?.items);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<LeadInput>(emptyLeadDraft);
  const [note, setNote] = useState("");
  const selectedLead = useQuery({
    queryKey: ["leads", selectedId],
    enabled: Boolean(selectedId),
    queryFn: () => apiFetch<LeadRecord>(`/api/leads/${selectedId}`)
  });

  useEffect(() => {
    if (!selectedId && items.length) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  useEffect(() => {
    const lead = selectedLead.data;
    if (lead) {
      setDraft({
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email,
        phone: lead.phone,
        status: lead.status,
        sourceId: lead.sourceId,
        sourceName: lead.sourceName,
        originDomain: lead.originDomain,
        pageUrl: lead.pageUrl,
        referrerUrl: lead.referrerUrl,
        utm: lead.utm ?? {},
        serviceInterest: lead.serviceInterest,
        note: lead.note,
        estimateSnapshot: lead.estimateSnapshot ?? {},
        emailConsent: lead.emailConsent,
        smsConsent: lead.smsConsent,
        lostReason: lead.lostReason
      });
    }
  }, [selectedLead.data]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["leads"] }),
      queryClient.invalidateQueries({ queryKey: ["leads", selectedId] })
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<LeadRecord>(selectedId ? `/api/leads/${selectedId}` : "/api/leads", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(draft)
      }),
    onSuccess: async (lead) => {
      setSelectedId(lead.id);
      await refresh();
    }
  });
  const statusMutation = useMutation({
    mutationFn: (status: LeadStatus) =>
      apiFetch<LeadRecord>(`/api/leads/${selectedId}/status`, {
        method: "POST",
        body: JSON.stringify({ status, lostReason: draft.lostReason, note: "" })
      }),
    onSuccess: refresh
  });
  const noteMutation = useMutation({
    mutationFn: () => apiFetch<LeadRecord>(`/api/leads/${selectedId}/notes`, { method: "POST", body: JSON.stringify({ note }) }),
    onSuccess: async () => {
      setNote("");
      await refresh();
    }
  });
  const convertMutation = useMutation({
    mutationFn: () => apiFetch<{ customerId: string; lead: LeadRecord }>(`/api/leads/${selectedId}/convert`, { method: "POST" }),
    onSuccess: refresh
  });

  return (
    <div className="leads-pipeline-grid">
      <Card
        title="Pipeline"
        eyebrow="Fixed stages"
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedId("");
              setDraft(emptyLeadDraft);
            }}
          >
            New lead
          </Button>
        }
      >
        <div className="leads-board">
          {leadStatuses.map((status) => {
            const columnLeads = items.filter((lead) => lead.status === status.value);
            return (
              <div className="leads-column" key={status.value}>
                <div className="leads-column__header">
                  <strong>{status.label}</strong>
                  <Badge tone={statusTone(status.value)}>{columnLeads.length}</Badge>
                </div>
                <div className="leads-column__list">
                  {columnLeads.map((lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      className={`leads-card ${selectedId === lead.id ? "is-active" : ""}`}
                      onClick={() => setSelectedId(lead.id)}
                    >
                      <strong>{leadName(lead)}</strong>
                      <span>{lead.serviceInterest || lead.sourceName || lead.email || lead.phone || "No details"}</span>
                      <small>{formatDateTime(lead.updatedAt)}</small>
                    </button>
                  ))}
                  {!columnLeads.length ? <p className="card-copy">No leads.</p> : null}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Lead Detail" : "Create Lead"}
          eyebrow={selectedLead.data ? statusLabel(selectedLead.data.status) : "Manual entry"}
          actions={<Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>{saveMutation.isPending ? "Saving..." : "Save lead"}</Button>}
        >
          <div className="lead-detail-grid">
            <Field label="First name"><input value={draft.firstName} onChange={(event) => setDraft({ ...draft, firstName: event.target.value })} /></Field>
            <Field label="Last name"><input value={draft.lastName} onChange={(event) => setDraft({ ...draft, lastName: event.target.value })} /></Field>
            <Field label="Email"><input value={draft.email ?? ""} onChange={(event) => setDraft({ ...draft, email: event.target.value })} /></Field>
            <Field label="Phone"><input value={draft.phone} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} /></Field>
            <Field label="Status">
              <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as LeadStatus })}>
                {leadStatuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
              </select>
            </Field>
            <Field label="Source">
              <select value={draft.sourceId} onChange={(event) => setDraft({ ...draft, sourceId: event.target.value })}>
                <option value="">No source</option>
                {sourceItems.map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}
              </select>
            </Field>
            <Field label="Service interest"><input value={draft.serviceInterest} onChange={(event) => setDraft({ ...draft, serviceInterest: event.target.value })} /></Field>
            <Field label="Origin domain"><input value={draft.originDomain} onChange={(event) => setDraft({ ...draft, originDomain: event.target.value })} /></Field>
            <Field label="Email consent">
              <label className="checkbox-row"><input type="checkbox" checked={draft.emailConsent} onChange={(event) => setDraft({ ...draft, emailConsent: event.target.checked })} /><span>Marketing email allowed</span></label>
            </Field>
            <Field label="SMS consent">
              <label className="checkbox-row"><input type="checkbox" checked={draft.smsConsent} onChange={(event) => setDraft({ ...draft, smsConsent: event.target.checked })} /><span>Marketing SMS allowed</span></label>
            </Field>
          </div>
          <Field label="Note"><textarea rows={4} value={draft.note} onChange={(event) => setDraft({ ...draft, note: event.target.value })} /></Field>
          {draft.status === "lost" ? <Field label="Lost reason"><input value={draft.lostReason} onChange={(event) => setDraft({ ...draft, lostReason: event.target.value })} /></Field> : null}
          {saveMutation.error ? <p className="error-text">{(saveMutation.error as Error).message}</p> : null}
        </Card>

        {selectedId ? (
          <Card title="Actions" eyebrow="Follow-up">
            <div className="lead-status-actions">
              {leadStatuses.map((status) => (
                <Button key={status.value} variant={draft.status === status.value ? "primary" : "secondary"} onClick={() => statusMutation.mutate(status.value)} disabled={statusMutation.isPending}>
                  {status.label}
                </Button>
              ))}
              <Button variant="secondary" onClick={() => convertMutation.mutate()} disabled={convertMutation.isPending || Boolean(selectedLead.data?.linkedCustomerId)}>
                {convertMutation.isPending ? "Converting..." : selectedLead.data?.linkedCustomerId ? "Customer linked" : "Convert to customer"}
              </Button>
            </div>
            <Field label="Add note"><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></Field>
            <Button variant="secondary" onClick={() => noteMutation.mutate()} disabled={!note.trim() || noteMutation.isPending}>Add note</Button>
            {statusMutation.error ? <p className="error-text">{(statusMutation.error as Error).message}</p> : null}
            {convertMutation.error ? <p className="error-text">{(convertMutation.error as Error).message}</p> : null}
          </Card>
        ) : null}

        {selectedLead.data?.events?.length ? (
          <Card title="Timeline" eyebrow="Lead activity">
            <DataTable
              columns={["When", "Event", "Message"]}
              rows={selectedLead.data.events.map((event) => [formatDateTime(event.createdAt), event.eventType.replace("lead.", ""), event.message || "-"])}
            />
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function LeadsCapture() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["settings", "leads"],
    queryFn: () => apiFetch<LeadsSettings>("/api/settings/leads")
  });
  const sources = useLeadSources();
  const [draft, setDraft] = useState<LeadsSettings>({ allowedCaptureDomains: [], defaultLeadSourceId: "", dedupeWindowDays: 90 });
  const [sourceDraft, setSourceDraft] = useState({ name: "", slug: "", channel: "web", active: true, config: {} as Record<string, unknown> });
  const [copiedSnippet, setCopiedSnippet] = useState("");
  const endpoint = typeof window === "undefined" ? "/api/public/leads/capture" : `${window.location.origin}/api/public/leads/capture`;

  useEffect(() => {
    if (settings.data) {
      setDraft(settings.data);
    }
  }, [settings.data]);

  const saveSettings = useMutation({
    mutationFn: () => apiFetch<LeadsSettings>("/api/settings/leads", { method: "PUT", body: JSON.stringify(draft) }),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["settings", "leads"] })
  });
  const saveSource = useMutation({
    mutationFn: () => apiFetch<{ id: string }>("/api/settings/lead-sources", { method: "POST", body: JSON.stringify(sourceDraft) }),
    onSuccess: async () => {
      setSourceDraft({ name: "", slug: "", channel: "web", active: true, config: {} });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["leads", "sources"] }),
        queryClient.invalidateQueries({ queryKey: ["settings", "lead-sources"] })
      ]);
    }
  });

  const newsletterSnippet = `fetch("${endpoint}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    captureType: "newsletter",
    source: "website_newsletter",
    email: emailAddress,
    firstName: firstName || "",
    emailConsent: true,
    pageUrl: window.location.href,
    referrerUrl: document.referrer
  })
});`;

  const estimatorSnippet = `fetch("${endpoint}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    captureType: "estimator",
    source: "instant_estimator",
    firstName,
    lastName,
    email,
    phone,
    emailConsent,
    smsConsent,
    serviceInterest: selectedService,
    estimateSnapshot: estimateResult,
    pageUrl: window.location.href,
    referrerUrl: document.referrer
  })
});`;

  async function copySnippet(kind: string, text: string) {
    await navigator.clipboard.writeText(text);
    setCopiedSnippet(kind);
    window.setTimeout(() => setCopiedSnippet((current) => (current === kind ? "" : current)), 1600);
  }

  return (
    <div className="grid two">
      <Card title="Capture Settings" eyebrow="Website domains" actions={<Button onClick={() => saveSettings.mutate()} disabled={saveSettings.isPending}>{saveSettings.isPending ? "Saving..." : "Save settings"}</Button>}>
        <Field label="Allowed capture domains" hint="Comma-separated hostnames. Do not include paths.">
          <input value={listToCsv(draft.allowedCaptureDomains)} onChange={(event) => setDraft({ ...draft, allowedCaptureDomains: csvToList(event.target.value) })} />
        </Field>
        <Field label="Default lead source">
          <select value={draft.defaultLeadSourceId} onChange={(event) => setDraft({ ...draft, defaultLeadSourceId: event.target.value })}>
            <option value="">No default source</option>
            {asArray<LeadSource>(sources.data?.items).map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}
          </select>
        </Field>
        <Field label="Dedupe window days">
          <input type="number" min="1" value={draft.dedupeWindowDays} onChange={(event) => setDraft({ ...draft, dedupeWindowDays: Number(event.target.value || 90) })} />
        </Field>
        {saveSettings.error ? <p className="error-text">{(saveSettings.error as Error).message}</p> : null}
      </Card>

      <Card title="Lead Sources" eyebrow="Attribution">
        <DataTable
          columns={["Source", "Slug", "Channel", "Active"]}
          rows={asArray<LeadSource>(sources.data?.items).map((source) => [
            source.name,
            source.slug,
            source.channel,
            source.active ? "Yes" : "No"
          ])}
        />
        <div className="grid two">
          <Field label="Name"><input value={sourceDraft.name} onChange={(event) => setSourceDraft({ ...sourceDraft, name: event.target.value })} /></Field>
          <Field label="Slug"><input value={sourceDraft.slug} onChange={(event) => setSourceDraft({ ...sourceDraft, slug: event.target.value })} /></Field>
          <Field label="Channel"><input value={sourceDraft.channel} onChange={(event) => setSourceDraft({ ...sourceDraft, channel: event.target.value })} /></Field>
          <Field label="Active"><label className="checkbox-row"><input type="checkbox" checked={sourceDraft.active} onChange={(event) => setSourceDraft({ ...sourceDraft, active: event.target.checked })} /><span>Active</span></label></Field>
        </div>
        <Button variant="secondary" onClick={() => saveSource.mutate()} disabled={!sourceDraft.name.trim() || saveSource.isPending}>Add source</Button>
      </Card>

      <Card
        title="Newsletter Popup"
        eyebrow="Embed snippet"
        actions={<Button variant="secondary" onClick={() => copySnippet("newsletter", newsletterSnippet)}>{copiedSnippet === "newsletter" ? "Copied" : "Copy"}</Button>}
      >
        <textarea className="code-textarea" readOnly rows={12} value={newsletterSnippet} spellCheck={false} wrap="soft" />
      </Card>
      <Card
        title="Instant Estimator"
        eyebrow="Embed snippet"
        actions={<Button variant="secondary" onClick={() => copySnippet("estimator", estimatorSnippet)}>{copiedSnippet === "estimator" ? "Copied" : "Copy"}</Button>}
      >
        <textarea className="code-textarea" readOnly rows={14} value={estimatorSnippet} spellCheck={false} wrap="soft" />
      </Card>
    </div>
  );
}

function LeadsAutomations() {
  const queryClient = useQueryClient();
  const automations = useQuery({
    queryKey: ["leads", "automations"],
    queryFn: () => apiFetch<{ items: LeadAutomationRecord[] }>("/api/leads/automations")
  });
  const templates = useQuery({
    queryKey: ["communications", "templates"],
    queryFn: () => apiFetch<{ items: EmailTemplateRecord[] }>("/api/communications/templates")
  });
  const sources = useLeadSources();
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<LeadAutomationInput>(emptyAutomationDraft);
  const items = asArray<LeadAutomationRecord>(automations.data?.items);
  const marketingTemplates = asArray<EmailTemplateRecord>(templates.data?.items).filter((template) => template.templateType === "marketing");

  useEffect(() => {
    if (!selectedId && items.length) {
      setSelectedId(items[0].id);
    }
  }, [items, selectedId]);

  useEffect(() => {
    const automation = items.find((item) => item.id === selectedId);
    if (automation) {
      setDraft({
        name: automation.name,
        triggerStatus: automation.triggerStatus,
        sourceIds: automation.sourceIds,
        stopOnBooking: automation.stopOnBooking !== false,
        steps: automation.steps,
        active: automation.active
      });
    }
  }, [items, selectedId]);

  const invalidate = async () => queryClient.invalidateQueries({ queryKey: ["leads", "automations"] });
  const normalizedDraft = normalizeAutomationDraft(draft);
  const canSave =
    normalizedDraft.name.trim() &&
    normalizedDraft.steps.length > 0 &&
    normalizedDraft.steps.every((step) => step.channel === "sms" || step.templateId);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<LeadAutomationRecord>(selectedId ? `/api/leads/automations/${selectedId}` : "/api/leads/automations", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(normalizedDraft)
      }),
    onSuccess: async (automation) => {
      setSelectedId(automation.id);
      await invalidate();
    }
  });
  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ success: boolean }>(`/api/leads/automations/${selectedId}`, { method: "DELETE" }),
    onSuccess: async () => {
      setSelectedId("");
      setDraft(emptyAutomationDraft);
      await invalidate();
    }
  });
  const activeMutation = useMutation({
    mutationFn: (active: boolean) => apiFetch<LeadAutomationRecord>(`/api/leads/automations/${selectedId}/${active ? "resume" : "pause"}`, { method: "POST" }),
    onSuccess: invalidate
  });

  const updateStep = (index: number, patch: Partial<LeadAutomationStepInput>) => {
    setDraft((current) => ({
      ...current,
      steps: current.steps.map((step, stepIndex) => stepIndex === index ? { ...step, ...patch } : step)
    }));
  };

  return (
    <div className="communications-shell">
      <Card title="Automations" eyebrow="Lead follow-up" actions={<Button variant="secondary" onClick={() => { setSelectedId(""); setDraft(emptyAutomationDraft); }}>New automation</Button>}>
        <div className="communications-list">
          {items.map((automation) => (
            <button key={automation.id} type="button" className={`communications-list__item ${selectedId === automation.id ? "is-active" : ""}`} onClick={() => setSelectedId(automation.id)}>
              <div>
                <strong>{automation.name}</strong>
                <p>
                  {statusLabel(automation.triggerStatus)} trigger / {automation.steps.length} step{automation.steps.length === 1 ? "" : "s"}
                  {automation.stopOnBooking !== false ? " / stops on booking" : ""}
                </p>
              </div>
              <Badge tone={automation.active ? "success" : "warning"}>{automation.active ? "Active" : "Paused"}</Badge>
            </button>
          ))}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Edit Automation" : "Create Automation"}
          eyebrow="Rule builder"
          actions={
            <div className="communications-card-actions">
              {selectedId ? <Button variant="ghost" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>Delete</Button> : null}
              <Button onClick={() => saveMutation.mutate()} disabled={!canSave || saveMutation.isPending}>{saveMutation.isPending ? "Saving..." : "Save automation"}</Button>
            </div>
          }
        >
          <div className="grid two">
            <Field label="Name"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
            <Field label="Trigger status">
              <select value={draft.triggerStatus} onChange={(event) => setDraft({ ...draft, triggerStatus: event.target.value as LeadStatus })}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="estimate_sent">Estimate Sent</option>
              </select>
            </Field>
            <Field label="Active">
              <label className="checkbox-row"><input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /><span>Enroll matching leads</span></label>
            </Field>
            <Field label="Stop condition">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={draft.stopOnBooking !== false}
                  onChange={(event) => setDraft({ ...draft, stopOnBooking: event.target.checked })}
                />
                <span>Stop when matching booking is created</span>
              </label>
            </Field>
          </div>

          <div className="stack">
            <p className="eyebrow">Source filters</p>
            <div className="tag-grid">
              {asArray<LeadSource>(sources.data?.items).map((source) => {
                const active = draft.sourceIds.includes(source.id);
                return (
                  <label key={source.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(event) => {
                        const next = event.target.checked
                          ? [...draft.sourceIds, source.id]
                          : draft.sourceIds.filter((id) => id !== source.id);
                        setDraft({ ...draft, sourceIds: next });
                      }}
                    />
                    <span>{source.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="stack">
            <div className="card__header">
              <div>
                <p className="eyebrow">Steps</p>
                <p className="card-copy">Delays are measured from when the lead enters the trigger status.</p>
              </div>
              <Button variant="secondary" onClick={() => setDraft({ ...draft, steps: [...draft.steps, { ...emptyAutomationStep, id: `step_${draft.steps.length + 1}` }] })}>Add step</Button>
            </div>
            {draft.steps.map((step, index) => (
              <div className="lead-step-row" key={step.id || index}>
                <Field label="Delay">
                  <input type="number" min="1" value={step.delayValue} onChange={(event) => updateStep(index, { delayValue: Number(event.target.value || 1) })} />
                </Field>
                <Field label="Unit">
                  <select value={step.delayUnit} onChange={(event) => updateStep(index, { delayUnit: event.target.value as LeadAutomationStepInput["delayUnit"] })}>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </Field>
                <Field label="Channel">
                  <select value={step.channel} onChange={(event) => updateStep(index, { channel: event.target.value as LeadAutomationStepInput["channel"] })}>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </Field>
                {step.channel === "email" ? (
                  <Field label="Template">
                    <select value={step.templateId} onChange={(event) => updateStep(index, { templateId: event.target.value })}>
                      <option value="">Choose template</option>
                      {marketingTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
                    </select>
                  </Field>
                ) : (
                  <Field label="SMS body">
                    <textarea rows={3} value={step.smsBody} onChange={(event) => updateStep(index, { smsBody: event.target.value })} />
                  </Field>
                )}
                {step.channel === "email" ? (
                  <div className="generated-offer-controls">
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={Boolean(step.generatedOffer?.enabled)}
                        onChange={(event) =>
                          updateStep(index, {
                            generatedOffer: {
                              ...normalizeGeneratedOffer(step.generatedOffer, "newsletter_first_booking"),
                              enabled: event.target.checked,
                              eligibilityMode: "newsletter_first_booking"
                            }
                          })
                        }
                      />
                      <span>Generated promo code</span>
                    </label>
                    {step.generatedOffer?.enabled ? (
                      <div className="grid three">
                        <Field label="Discount %">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={step.generatedOffer.discountValue}
                            onChange={(event) =>
                              updateStep(index, {
                                generatedOffer: {
                                  ...normalizeGeneratedOffer(step.generatedOffer, "newsletter_first_booking"),
                                  discountValue: Number(event.target.value || 10)
                                }
                              })
                            }
                          />
                        </Field>
                        <Field label="Prefix">
                          <input
                            value={step.generatedOffer.codePrefix}
                            onChange={(event) =>
                              updateStep(index, {
                                generatedOffer: {
                                  ...normalizeGeneratedOffer(step.generatedOffer, "newsletter_first_booking"),
                                  codePrefix: event.target.value
                                }
                              })
                            }
                          />
                        </Field>
                        <Field label="Expires in days">
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={step.generatedOffer.expirationDays}
                            onChange={(event) =>
                              updateStep(index, {
                                generatedOffer: {
                                  ...normalizeGeneratedOffer(step.generatedOffer, "newsletter_first_booking"),
                                  expirationDays: Number(event.target.value || 7)
                                }
                              })
                            }
                          />
                        </Field>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <Button variant="ghost" onClick={() => setDraft({ ...draft, steps: draft.steps.filter((_step, stepIndex) => stepIndex !== index) })} disabled={draft.steps.length <= 1}>Remove</Button>
              </div>
            ))}
          </div>
          {saveMutation.error ? <p className="error-text">{(saveMutation.error as Error).message}</p> : null}
        </Card>

        {selectedId ? (
          <Card title="Status" eyebrow="Automation pipeline">
            {items.find((item) => item.id === selectedId) ? (
              <div className="grid three">
                <div className="booking-meta-card"><p className="eyebrow">Pending</p><strong>{items.find((item) => item.id === selectedId)?.pendingCount ?? 0}</strong></div>
                <div className="booking-meta-card"><p className="eyebrow">Sent</p><strong>{items.find((item) => item.id === selectedId)?.sentCount ?? 0}</strong></div>
                <div className="booking-meta-card"><p className="eyebrow">Next due</p><strong>{formatDateTime(items.find((item) => item.id === selectedId)?.nextDueAt ?? "")}</strong></div>
              </div>
            ) : null}
            <div className="communications-card-actions">
              <Button variant="secondary" onClick={() => activeMutation.mutate(false)} disabled={activeMutation.isPending}>Pause</Button>
              <Button variant="secondary" onClick={() => activeMutation.mutate(true)} disabled={activeMutation.isPending}>Resume</Button>
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function LeadsActivity() {
  const activity = useQuery({
    queryKey: ["leads", "activity"],
    queryFn: () => apiFetch<{ items: LeadEventRecord[] }>("/api/leads/activity")
  });
  return (
    <Card title="Lead Activity" eyebrow="Timeline">
      <DataTable
        columns={["When", "Lead", "Event", "Message"]}
        rows={asArray<LeadEventRecord>(activity.data?.items).map((event) => [
          formatDateTime(event.createdAt),
          event.leadName || event.leadEmail || event.leadPhone || event.leadId,
          event.eventType.replace("lead.", ""),
          event.message || "-"
        ])}
      />
    </Card>
  );
}
